import { useState } from "react"
import Link from "next/link"
import dynamic from "next/dynamic"
import cn from "classnames"
import { getGlobals } from "storyblok/api"
import { getAllPracticeAreaPaths, getCasesByPracticeArea, getIndividualPracticeArea } from "storyblok/practiceAreas"
import Meta from "components/Meta"
import PageHeader from "components/DynamicComponent/molecules/PageHeader"
import SidebarForm from "components/SidebarForm"
import Arrow from "public/assets/down-arrow-blue-background.svg"
import AdditionalInformation from "components/Archive/AdditionalInformation"
import WinsAndReviews from "components/Archive/WinsAndReviews"
import StoryblokVisualEditor from "components/StoryblokVisualEditor"
import CTABanner from "components/DynamicComponent/molecules/CTABanner"
import richText from "utilities/richText"
import isRichTextValid from "utilities/isRichTextValid"
import PracticeAreasFAQSection from "components/Archive/PracticeAreasFAQSection"

export default function IndividualPracticeArea({ story, caseWins, meta }) {
  const [open, setOpen] = useState(false)

  const PracticeAreaChildArchive =
    story?.content?.childPracticeAreas?.length > 0
      ? dynamic(() => import("../../components/Archive/PracticeAreaChildArchive"))
      : () => null

  const Content = ({ hash, content }) =>
    isRichTextValid(content) ? (
      <div
        id={hash}
        data-pagefind-weight="2"
        className="pb-8 prose-h2:mb-4 prose-h2:text-primary prose-h3:text-m2 prose-h3:font-semibold prose-h3:pb-3 scroll-mt-12 xl:scroll-mt-4"
      >
        {richText(content)}
      </div>
    ) : null

  return (
    <>
      <Meta info={meta} />
      <main className="page-content" data-pagefind-body data-pagefind-sort="weight[data-weight]" data-weight="9">
        <StoryblokVisualEditor story={story?.content}>
          <PageHeader
            blok={{
              type: story?.content?.image?.filename?.length ? "gradient" : "standard",
              image: story?.content?.image,
              heading:
                story?.content?.title && story?.content?.title !== "" ? story?.content?.title : story?.content?.name,
              breadcrumbs: story?.content?.breadcrumbs,
            }}
          />
          <section className="xl:py-16">
            <div className="individual-practice-area">
              <div className="table-of-contents sticky top-0 xl:top-12 z-30">
                {story?.content?.bodyContent?.length > 0 ? (
                  <nav className="table-of-contents bg-grey-cool-tint xl:p-7 xl:ml-5">
                    <div className="flex flex-col">
                      <button
                        className={cn("self-start py-2 px-5 xl:hidden w-full text-left", {
                          "scale-y-0": open,
                          "scale-y-100": !open,
                        })}
                        onClick={() => setOpen(!open)}
                      >
                        Jump to
                      </button>

                      <div
                        className={cn(
                          "absolute xl:relative bg-grey-cool-tint w-full xl:w-auto transition-all duration-300 origin-top p-5 xl:p-0",
                          {
                            "scale-y-0 xl:scale-y-100": !open,
                            "scale-y-100": open,
                          },
                        )}
                      >
                        <h3 className="font-primary hidden xl:block text-m1 font-semibold pb-5">Table of Contents</h3>
                        <ul className="flex flex-col gap-5 list-none">
                          {story?.content?.bodyContent?.map(({ hash, name }) => (
                            <li key={`${hash}-link`}>
                              <Link
                                href={`#${hash}`}
                                className="underline hover:no-underline text-primary"
                                onClick={() => setOpen(false)}
                                scroll={false}
                              >
                                {name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <button
                        className="absolute right-0 top-0 xl:hidden z-30"
                        onClick={() => setOpen(!open)}
                        title="Filter Practice Areas by Title"
                      >
                        <Arrow
                          className={cn("h-10", {
                            "rotate-180": open,
                          })}
                        />
                      </button>
                    </div>
                  </nav>
                ) : null}
                {story?.content?.relatedBlogPages ? (
                  <AdditionalInformation links={story?.content?.relatedBlogPages} className="hidden xl:block" />
                ) : null}
              </div>
              {story?.content?.relatedBlogPages ? (
                <AdditionalInformation links={story?.content?.relatedBlogPages} className="xl:hidden" />
              ) : null}
              <div className="practice-area-content basis-1/2 px-5 pt-8 xl:pt-0 xl:px-0">
                <Content hash="overview" content={story?.content?.overview} />
                <Content hash="client-review" content={story?.content?.client_review} />
                {story?.content?.frequently_asked_questions?.length ? (
                  <div id="frequently-asked-questions" className="pb-8 scroll-mt-12 xl:scroll-mt-4">
                    <h2 className="mb-4 text-primary">Frequently Asked Questions</h2>
                    <PracticeAreasFAQSection questions={story?.content?.frequently_asked_questions} />
                  </div>
                ) : null}
                <Content hash="resources" content={story?.content?.resources} />
              </div>
              <div className="practice-area-sidebar-form lg:sticky top-12 z-10">
                <SidebarForm />
                <WinsAndReviews caseWins={caseWins} name={story?.content?.name} className="hidden xl:block" />
              </div>
              <WinsAndReviews caseWins={caseWins} name={story?.content?.name} className="xl:hidden" />
            </div>
          </section>
          <PracticeAreaChildArchive items={story?.content?.childPracticeAreas} name={story?.content?.name} />
        </StoryblokVisualEditor>

        <section className="hidden xl:block">
          <CTABanner />
        </section>
      </main>
    </>
  )
}

export async function getStaticProps({ params: { practice }, preview = null }) {
  const globals = await getGlobals("exclude-global-sections")

  const slug = `practice-areas/${practice.join("/")}`

  const page = await getIndividualPracticeArea(slug, preview)

  const caseWins = await getCasesByPracticeArea(page.content.uuid)

  return {
    props: {
      slug,
      story: page ?? null,
      caseWins: caseWins ?? null,
      meta: page?.content?.twentysixty_seo ?? null,
      globals: globals ?? null,
      preview,
    },
  }
}

export async function getStaticPaths() {
  return {
    paths: await getAllPracticeAreaPaths(),
    fallback: false,
  }
}
